// React
import { useState } from "react";

// Libraries
import * as yup from 'yup';
import toast, { Toaster } from 'react-hot-toast';
import { toastDefs, toastDefsError } from '../../config/toastConfig';

// Components
import InputDefault from "../../components/form/InputDefault";
import ButtonDefault from "../../components/form/ButtonDefault";

// Icons
import { IconClosedEye, IconEyeOpen } from "../../components/icons";

// Services
import api from "../../services/api";

// Styles
import { comum } from "../../styles/Themes/light";

interface Errors {
    senha?: string;
    confirmeSenha?: string;
}

interface ChangePasswordApplicantsProps {
    applicantId: string;
    onCancel: () => void;
}

export default function ChangePasswordApplicants({ applicantId, onCancel }: ChangePasswordApplicantsProps) {
    const [changeVisibility, setChangeVisibility] = useState<boolean>(true);
    const [DTOPass, setDTOPass] = useState({
        senha: '',
        confirmeSenha: '',
    });
    const [advancePageError, setAdvancePageError] = useState<Errors>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    function handleOnChange(name: string, value: string) {
        const newDTO: any = DTOPass;
        newDTO[name] = value;
        setDTOPass({ ...newDTO });
        
        // Limpar erro do campo quando usuário começar a digitar
        if (advancePageError[name as keyof Errors]) {
            setAdvancePageError(prev => ({ ...prev, [name]: '' }));
        }
    }

    async function updateApplicantPassword() {
        try {
            setIsLoading(true);
            setAdvancePageError({});

            const schema = yup.object().shape({
                senha: yup.string()
                    .required('Senha é obrigatória')
                    .min(6, 'Senha deve ter pelo menos 6 caracteres')
                    .matches(
                        /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/,
                        "Senha deve conter pelo menos 1 maiúscula, 1 número e 1 caractere especial"
                    ),
                confirmeSenha: yup.string()
                    .required('Confirmação de senha é obrigatória')
                    .oneOf([yup.ref('senha')], 'As senhas não conferem'),
            });

            await schema.validate(DTOPass, {
                abortEarly: false,
            });

            const response = await api.put(`/solicitante/senha/alterar/${applicantId}`, DTOPass);

            if (response.data.status !== 'success') {
                if (response.data.result && response.data.result[0]) {
                    throw new Error(response.data.result[0].message || response.data.result[0].error);
                } else {
                    throw new Error(response.data.message || 'Erro ao alterar senha');
                }
            }

            toast.success('Senha alterada com sucesso!', toastDefs);

            setTimeout(() => {
                onCancel();
            }, 1500);

        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                const errors: any = {};
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        errors[error.path] = error.message;
                    }
                });
                setAdvancePageError(errors);
            } else {
                toast.error(err.message || 'Erro ao alterar senha', toastDefsError);
            }
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="">
            <div className="mb-6">
                <h2 
                    className="text-xl font-semibold mb-2"
                    style={{ color: comum.colors.interfaceTextPrimary }}
                >
                    Alterar Senha
                </h2>
                <p 
                    className="text-sm"
                    style={{ color: comum.colors.interfaceGray400 }}
                >
                    Crie uma senha forte. Utilize ao menos 6 dígitos, contendo 1 letra maiúscula, 1 número e 1 caractere especial.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="relative">
                    <InputDefault
                        title="Nova Senha:"
                        placeholder="Digite sua nova senha"
                        name="senha"
                        value={DTOPass.senha}
                        type={changeVisibility ? 'password' : 'text'}
                        onChangeText={(value) => handleOnChange('senha', value)}
                        className={advancePageError.senha ? 'error' : ''}
                        error={advancePageError.senha}
                    />
                    <div
                        className="absolute right-3 top-9 cursor-pointer"
                        style={{ color: comum.colors.interfaceGray500 }}
                        onClick={() => setChangeVisibility(!changeVisibility)}
                    >
                        {changeVisibility ? <IconEyeOpen /> : <IconClosedEye />}
                    </div>
                </div>

                <div className="relative">
                    <InputDefault
                        title="Confirme a Nova Senha:"
                        placeholder="Confirme sua nova senha"
                        name="confirmeSenha"
                        type={changeVisibility ? 'password' : 'text'}
                        value={DTOPass.confirmeSenha}
                        onChangeText={(value) => handleOnChange('confirmeSenha', value)}
                        className={advancePageError.confirmeSenha ? 'error' : ''}
                        error={advancePageError.confirmeSenha}
                    />
                    <div
                        className="absolute right-3 top-9 cursor-pointer"
                        style={{ color: comum.colors.interfaceGray500 }}
                        onClick={() => setChangeVisibility(!changeVisibility)}
                    >
                        {changeVisibility ? <IconEyeOpen /> : <IconClosedEye />}
                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-4 mt-8">
                <div className="" style={{width: '120px'}}>
                    <ButtonDefault
                        className="cancel"
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        Cancelar
                    </ButtonDefault>
                </div>
                <div className="" style={{width: '140px'}}>
                    <ButtonDefault
                        className="save"
                        onClick={updateApplicantPassword}
                        loading={isLoading}
                        disabled={isLoading}
                    >
                        Salvar Senha
                    </ButtonDefault>
                </div>
            </div>

            <div onClick={() => toast.remove()}>
                <Toaster />
            </div>
        </div>
    );
}